import { Feather } from "@expo/vector-icons";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type CountryDropdownOption = {
  value: string;
  label: string;
  iso: string;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  options: CountryDropdownOption[];
  placeholder: string;
  disabled?: boolean;
  /** When true, the trigger is sized for a phone-prefix select (narrower). */
  compact?: boolean;
};

type ListboxRect = { top: number; left: number; width: number };

export default function CountryDropdown({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  compact,
}: Props) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<ListboxRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((o) => o.value === value);

  const updateRect = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const desiredWidth = compact ? Math.max(r.width, 220) : r.width;
    setRect({
      top: r.bottom + 4,
      left: r.left,
      width: desiredWidth,
    });
  }, [compact]);

  useLayoutEffect(() => {
    if (!open) return;
    updateRect();
  }, [open, updateRect]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScrollOrResize = () => updateRect();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updateRect]);

  const triggerStyle: React.CSSProperties = {
    width: compact ? 120 : "100%",
    minWidth: 0,
    padding: "10px 36px 10px 10px",
    borderRadius: 5,
    border: "1px solid #ccc",
    backgroundColor: "white",
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: disabled ? "not-allowed" : "pointer",
    color: "#000",
    textAlign: "left",
    position: "relative",
    opacity: disabled ? 0.6 : 1,
    fontFamily: "inherit",
    boxSizing: "border-box",
  };

  const flagImg = (iso: string) => (
    <img
      src={`https://flagcdn.com/24x18/${iso}.png`}
      srcSet={`https://flagcdn.com/48x36/${iso}.png 2x`}
      width={24}
      height={18}
      alt=""
      style={{ display: "block", flexShrink: 0 }}
    />
  );

  const listbox =
    open && rect && typeof document !== "undefined"
      ? createPortal(
          <ul
            ref={listRef}
            role="listbox"
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: rect.width,
              maxHeight: 280,
              overflowY: "auto",
              background: "white",
              border: "1px solid #ccc",
              borderRadius: 5,
              boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
              listStyle: "none",
              padding: 4,
              margin: 0,
              zIndex: 2147483647,
            }}
          >
            {options.map((o) => {
              const isSelected = o.value === value;
              return (
                <li
                  key={o.value}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: 4,
                    cursor: "pointer",
                    background: isSelected ? "#eef2ff" : "transparent",
                    color: "#000",
                    fontSize: 16,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLLIElement).style.background =
                        "#f5f5f5";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLLIElement).style.background =
                        "transparent";
                  }}
                >
                  {flagImg(o.iso)}
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {o.label}
                  </span>
                </li>
              );
            })}
          </ul>,
          document.body,
        )
      : null;

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: compact ? 120 : "100%",
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={triggerStyle}
      >
        {selected ? (
          <>
            {flagImg(selected.iso)}
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {selected.label}
            </span>
          </>
        ) : (
          <span style={{ color: "#888", flex: 1 }}>{placeholder}</span>
        )}
        <span
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            display: "flex",
          }}
        >
          <Feather name="chevron-down" size={16} color="#555" />
        </span>
      </button>
      {listbox}
    </div>
  );
}
